<template>
  <div class="ledger-shell">
    <!-- Global toast -->
    <transition name="toast">
      <div v-if="toast.message" class="ledger-toast" :class="'is-' + (toast.type || 'info')">
        <span class="dot"></span>
        <span>{{ toast.message }}</span>
      </div>
    </transition>

    <!-- Header -->
    <header class="ledger-header">
      <div>
        <div class="eyebrow">Finance</div>
        <h1>Ledger</h1>
        <p class="subtitle">Track settled & live balances across branches and clients.</p>
      </div>
      <div class="header-actions">
        <!--
          Help guide — surfaced for the two self-view roles (branch + client).
          Vendors get the full ledger index instead; hiding the button for
          them keeps their header uncluttered.
        -->
        <button
          v-if="!isVendor"
          type="button"
          class="btn btn-secondary"
          @click="showHelp = true"
          title="How to read your ledger"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Help &amp; Guide
        </button>
        <button
          v-if="isVendor"
          class="btn btn-primary"
          :disabled="!selectedEntity"
          @click="openManualForm"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
            <path d="M12 5v14M5 12h14" stroke-linecap="round" />
          </svg>
          Manual Entry
        </button>
      </div>
    </header>

    <!--
      Guide modal — teleported to body internally, so mounting it here has
      no layout effect. Only rendered for the two self-view roles; vendor
      never sees it. `role` prop drives the direction-rule table so a
      branch reads pay-in as debit while a client reads it as credit.
    -->
    <ledger-help-guide
      v-if="!isVendor"
      :show="showHelp"
      :role="role === 'subadmin' ? 'branch' : 'client'"
      @close="showHelp = false"
    />

    <div v-if="loading && !selectedEntity" class="loading-block">Loading ledger…</div>

    <div v-else class="ledger-body" :class="{ 'is-vendor': isVendor }">
      <!-- Vendor sidebar -->
      <aside v-if="isVendor" class="entity-panel">
        <div class="search-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" stroke-linecap="round" />
          </svg>
          <input
            v-model="entitySearch"
            type="text"
            placeholder="Search branch or client…"
            class="search-input"
          />
        </div>

        <div class="entity-groups">
          <section class="entity-group">
            <div class="group-head">
              <span class="group-title">Branches</span>
              <span class="group-count">{{ filteredBranches.length }}</span>
            </div>
            <div class="entity-list">
              <div
                v-for="b in filteredBranches"
                :key="'b' + b.id"
                class="entity-row"
                :class="{ 'is-active': isSelected('branch', b.id) }"
                @click="selectEntity('branch', b)"
              >
                <div class="avatar avatar-branch">{{ initialsOf(b) }}</div>
                <div class="entity-meta">
                  <div class="entity-name">{{ b.name || b.username }}</div>
                  <div class="entity-sub">{{ b.username }}</div>
                </div>
                <div class="entity-balance">
                  <div class="balance-label">Live</div>
                  <div class="balance-value" :class="signClass(b.live)">{{ formatMoney(b.live) }}</div>
                </div>
              </div>
              <div v-if="filteredBranches.length === 0" class="empty-mini">No branches match.</div>
            </div>
          </section>

          <section class="entity-group">
            <div class="group-head">
              <span class="group-title">Clients</span>
              <span class="group-count">{{ filteredClients.length }}</span>
            </div>
            <div class="entity-list">
              <div
                v-for="c in filteredClients"
                :key="'c' + c.id"
                class="entity-row"
                :class="{ 'is-active': isSelected('client', c.id) }"
                @click="selectEntity('client', c)"
              >
                <div class="avatar avatar-client">{{ initialsOf(c) }}</div>
                <div class="entity-meta">
                  <div class="entity-name">{{ c.name || c.username }}</div>
                  <div class="entity-sub">{{ c.username }}</div>
                </div>
                <div class="entity-balance">
                  <div class="balance-label">Live</div>
                  <div class="balance-value" :class="signClass(c.live)">{{ formatMoney(c.live) }}</div>
                </div>
              </div>
              <div v-if="filteredClients.length === 0" class="empty-mini">No clients match.</div>
            </div>
          </section>
        </div>
      </aside>

      <!-- Main area -->
      <main class="ledger-main">
        <div v-if="!selectedEntity" class="empty-hero">
          <div class="empty-hero-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M4 6h16v12H4z" />
              <path d="M4 10h16M8 14h4" stroke-linecap="round" />
            </svg>
          </div>
          <div class="empty-hero-title">Select an entity to view its ledger</div>
          <div class="empty-hero-sub">Pick a branch or client from the left panel.</div>
        </div>

        <template v-else>
          <!-- Balance card (compact) -->
          <section class="balance-card" :class="'hero-' + selectedEntity.type"
                   @click="showBreakdown = !showBreakdown">
            <div class="bc-left">
              <div class="bc-identity">
                <span class="bc-badge">{{ selectedEntity.type }}</span>
                <div class="bc-name">{{ selectedEntity.name || selectedEntity.username }}</div>
                <span class="bc-sub">@{{ selectedEntity.username }}</span>
              </div>
              <div class="bc-live">
                <span class="bc-live-label">Live balance</span>
                <span class="bc-live-value">{{ formatMoneyWithSign(balance.live) }}</span>
              </div>
            </div>
            <div class="bc-right">
              <div class="bc-mini">
                <span class="mini-label">Settled</span>
                <span class="mini-value">{{ formatMoneyWithSign(balance.settled) }}</span>
              </div>
              <div class="bc-divider"></div>
              <div class="bc-mini">
                <span class="mini-label">Pending</span>
                <span class="mini-value">{{ formatMoneyWithSign(balance.unsettledAccrual) }}</span>
              </div>
              <button class="bc-refresh" @click.stop="refreshCurrent" title="Refresh">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke-linecap="round" />
                  <path d="M20 4v5h-5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
            </div>
          </section>

          <!-- Pending breakdown (click balance card to toggle) -->
          <transition name="explain-fade">
            <section v-if="showBreakdown" class="pending-explainer">
              <div class="explain-head">
                <div>
                  <div class="explain-title">How Pending is calculated</div>
                  <div class="explain-sub" v-if="balance.unsettledWindowStart">
                    Includes approved orders since <b>{{ formatDateShort(balance.unsettledWindowStart) }}</b> that haven't been settled yet.
                  </div>
                  <div class="explain-sub" v-else>
                    Post an <b>Opening Balance</b> for this entity to start tracking pending activity.
                  </div>
                </div>
                <button class="explain-close" @click="showBreakdown = false" aria-label="Close">×</button>
              </div>

              <div v-if="hasBreakdownActivity" class="explain-grid">
                <div class="explain-cell">
                  <div class="cell-label">Pay-in</div>
                  <div class="cell-value">{{ formatMoneyPlain(bd.payinAmount) }}</div>
                  <div class="cell-sub">{{ bd.payinCount }} {{ bd.payinCount === 1 ? 'order' : 'orders' }}</div>
                </div>
                <div class="explain-cell">
                  <div class="cell-label">Pay-out</div>
                  <div class="cell-value">{{ formatMoneyPlain(bd.payoutAmount) }}</div>
                  <div class="cell-sub">{{ bd.payoutCount }} {{ bd.payoutCount === 1 ? 'order' : 'orders' }}</div>
                </div>
                <div class="explain-cell">
                  <div class="cell-label">Pay-in comm</div>
                  <div class="cell-value">{{ formatMoneyPlain(bd.payinCommission) }}</div>
                  <div class="cell-sub">at snapshot rate</div>
                </div>
                <div class="explain-cell">
                  <div class="cell-label">Pay-out comm</div>
                  <div class="cell-value">{{ formatMoneyPlain(bd.payoutCommission) }}</div>
                  <div class="cell-sub">at snapshot rate</div>
                </div>
              </div>

              <div v-if="hasBreakdownActivity" class="explain-formula">
                <span class="formula-label">Net</span>
                <span class="formula-body">{{ formulaText }}</span>
                <span class="formula-eq">=</span>
                <span class="formula-result" :class="signClass(balance.unsettledAccrual)">
                  {{ formatMoneyWithSign(balance.unsettledAccrual) }}
                </span>
              </div>

              <div v-else class="explain-empty">No unsettled orders yet.</div>
            </section>
          </transition>

          <!-- Filters -->
          <section class="filters-bar">
            <div class="filter-presets">
              <button
                v-for="p in datePresets"
                :key="p.key"
                class="chip"
                :class="{ 'chip-active': activePreset === p.key }"
                @click="applyPreset(p.key)"
              >{{ p.label }}</button>
            </div>
            <div class="filter-inputs">
              <label class="filter-field">
                <span>From</span>
                <input type="date" v-model="filters.startDate" @change="onCustomDate" />
              </label>
              <label class="filter-field">
                <span>To</span>
                <input type="date" v-model="filters.endDate" @change="onCustomDate" />
              </label>
              <label class="filter-field">
                <span>Type</span>
                <select v-model="filters.entry_type" @change="loadEntries">
                  <option value="">All</option>
                  <option value="settlement">Settlement</option>
                  <option value="reversal">Reversal</option>
                  <option value="manual">Manual</option>
                </select>
              </label>
            </div>
          </section>

          <!-- Entries list -->
          <section class="entries-section">
            <div v-if="entries.length === 0" class="empty-entries">
              <div class="empty-mini-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v4M12 16h.01" stroke-linecap="round" />
                </svg>
              </div>
              <div class="empty-title">No entries in this range</div>
              <div class="empty-sub">Try widening the date range or removing the type filter.</div>
            </div>

            <div v-else class="entries-list">
              <div v-for="e in entries" :key="e.id" class="entry-card" :class="'type-' + e.entry_type">
                <div class="entry-left">
                  <div class="entry-icon" :class="'icon-' + e.entry_type">
                    <svg v-if="e.entry_type === 'settlement'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 12h18M3 6h18M3 18h18" stroke-linecap="round" />
                    </svg>
                    <svg v-else-if="e.entry_type === 'reversal'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M12 20V4M6 10l6-6 6 6" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </div>
                  <div class="entry-headline">
                    <div class="entry-title-row">
                      <span class="entry-title">{{ titleFor(e) }}</span>
                      <span class="pill pill-sm" :class="'pill-' + e.direction">{{ e.direction }}</span>
                    </div>
                    <div class="entry-meta">
                      <span>{{ e.entry_date }}</span>
                      <span v-if="e.settlement_for_date" class="dot-sep">•</span>
                      <span v-if="e.settlement_for_date">for {{ e.settlement_for_date }}</span>
                      <span v-if="e.references_order_id" class="dot-sep">•</span>
                      <span v-if="e.references_order_id" class="mono">order {{ e.references_order_id.slice(0, 12) }}…</span>
                      <span v-if="e.created_by" class="dot-sep">•</span>
                      <span v-if="e.created_by">by {{ e.created_by }}</span>
                    </div>
                    <div v-if="e.notes" class="entry-notes">{{ e.notes }}</div>
                  </div>
                </div>

                <div class="entry-right">
                  <div class="entry-amount" :class="e.direction === 'credit' ? 'amt-credit' : 'amt-debit'">
                    <span class="amt-sign">{{ e.direction === 'credit' ? '+' : '−' }}</span>
                    <span class="amt-value">{{ formatMoney(e.amount) }}</span>
                  </div>
                  <div v-if="e.entry_type === 'settlement'" class="entry-breakdown">
                    <div class="brk">
                      <span class="brk-lbl">Payin</span>
                      <span class="brk-val">{{ formatMoney(e.total_payin_amount) }} <em>· {{ e.total_payin_count }}</em></span>
                    </div>
                    <div class="brk">
                      <span class="brk-lbl">Payout</span>
                      <span class="brk-val">{{ formatMoney(e.total_payout_amount) }} <em>· {{ e.total_payout_count }}</em></span>
                    </div>
                    <div class="brk">
                      <span class="brk-lbl">Comm P/I</span>
                      <span class="brk-val">{{ formatMoney(e.total_payin_commission) }}</span>
                    </div>
                    <div class="brk">
                      <span class="brk-lbl">Comm P/O</span>
                      <span class="brk-val">{{ formatMoney(e.total_payout_commission) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="entries.length > 0" class="pagination-bar">
              <div class="pag-info">
                Showing page {{ pagination.page }} of {{ totalPages }} · {{ pagination.total }} entries
              </div>
              <div class="pag-controls">
                <button class="btn btn-ghost btn-sm" :disabled="pagination.page <= 1" @click="gotoPage(pagination.page - 1)">Prev</button>
                <button class="btn btn-ghost btn-sm" :disabled="pagination.page >= totalPages" @click="gotoPage(pagination.page + 1)">Next</button>
              </div>
            </div>
          </section>
        </template>
      </main>
    </div>

    <!-- Manual entry modal -->
    <teleport to="body">
      <transition name="modal-fade">
        <div v-if="showManualForm" class="modal-backdrop" @click.self="closeManualForm">
          <div class="modal-card">
            <div class="modal-head">
              <div>
                <div class="modal-eyebrow">{{ selectedEntity?.type?.toUpperCase() }} · {{ selectedEntity?.name || selectedEntity?.username }}</div>
                <div class="modal-title">Post Manual Entry</div>
              </div>
              <button class="modal-close" @click="closeManualForm" aria-label="Close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" />
                </svg>
              </button>
            </div>

            <div class="modal-body">
              <div class="form-grid">
                <div class="form-field">
                  <label>Category</label>
                  <select v-model="manualForm.category_id" @change="onCategoryChange">
                    <option value="">Select…</option>
                    <option v-for="c in usableCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>
                </div>

                <div class="form-field">
                  <label>Direction</label>
                  <div class="direction-toggle">
                    <button
                      type="button"
                      class="dir-btn"
                      :class="{ 'dir-active dir-credit': manualForm.direction === 'credit' }"
                      @click="manualForm.direction = 'credit'"
                    >Credit</button>
                    <button
                      type="button"
                      class="dir-btn"
                      :class="{ 'dir-active dir-debit': manualForm.direction === 'debit' }"
                      @click="manualForm.direction = 'debit'"
                    >Debit</button>
                  </div>
                </div>

                <div class="form-field span-2">
                  <label>Amount (₹)</label>
                  <input type="number" step="0.01" min="0" v-model.number="manualForm.amount" placeholder="0.00" />
                </div>

                <div class="form-field span-2">
                  <label>Date (IST)</label>
                  <input type="date" v-model="manualForm.entry_date" />
                </div>

                <div class="form-field span-2">
                  <label>Notes <span class="opt">(optional)</span></label>
                  <textarea rows="3" v-model="manualForm.notes" placeholder="Reason or reference…"></textarea>
                </div>
              </div>
            </div>

            <div class="modal-foot">
              <button class="btn btn-ghost" @click="closeManualForm">Cancel</button>
              <button class="btn btn-primary" :disabled="!canSubmitManual || submitting" @click="submitManual">
                {{ submitting ? 'Posting…' : 'Post Entry' }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import LedgerHelpGuide from '../../components/ledger/LedgerHelpGuide.vue';

const PRESETS = [
  { key: 'today',      label: 'Today' },
  { key: 'yesterday',  label: 'Yesterday' },
  { key: '7d',         label: '7 days' },
  { key: '30d',        label: '30 days' },
  { key: 'custom',     label: 'Custom' },
];

export default {
  name: 'Ledger',
  components: { LedgerHelpGuide },
  data() {
    return {
      loading: true,
      submitting: false,
      toast: { type: '', message: '' },
      entitySearch: '',
      activePreset: '30d',
      filters: { startDate: '', endDate: '', entry_type: '' },
      showBreakdown: false,
      showManualForm: false,
      // Slide-in help guide (branch/client only — hidden for vendor).
      showHelp: false,
      manualForm: {
        category_id: '',
        direction: 'credit',
        amount: 0,
        entry_date: this.todayISO(),
        notes: '',
      },
      datePresets: PRESETS,
    };
  },
  computed: {
    role()          { return this.$store.getters.role; },
    isVendor()      { return this.role === 'admin' || this.role === 'superadmin'; },
    summary()       { return this.$store.getters['ledger/summary']; },
    selectedEntity(){ return this.$store.getters['ledger/selectedEntity']; },
    entries()       { return this.$store.getters['ledger/entries']; },
    pagination()    { return this.$store.getters['ledger/pagination']; },
    balance()       { return this.$store.getters['ledger/balance']; },
    categories()    { return this.$store.getters['ledger/categories']; },
    totalPages()    { return Math.max(1, Math.ceil((this.pagination.total || 0) / (this.pagination.limit || 50))); },

    filteredBranches() {
      const q = this.entitySearch.trim().toLowerCase();
      if (!q) return this.summary.branches;
      return this.summary.branches.filter((b) =>
        (b.name || '').toLowerCase().includes(q) ||
        (b.username || '').toLowerCase().includes(q)
      );
    },
    filteredClients() {
      const q = this.entitySearch.trim().toLowerCase();
      if (!q) return this.summary.clients;
      return this.summary.clients.filter((c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.username || '').toLowerCase().includes(q)
      );
    },
    usableCategories() {
      if (!this.selectedEntity) return [];
      const type = this.selectedEntity.type;
      return this.categories.filter((c) => c.applicable_to === 'both' || c.applicable_to === type);
    },
    canSubmitManual() {
      return this.selectedEntity && this.manualForm.category_id &&
             this.manualForm.direction && Number(this.manualForm.amount) > 0;
    },
    bd() {
      return this.balance.unsettledBreakdown || {
        payinAmount: 0, payinCount: 0,
        payoutAmount: 0, payoutCount: 0,
        payinCommission: 0, payoutCommission: 0,
      };
    },
    hasBreakdownActivity() {
      return (this.bd.payinCount || 0) > 0 || (this.bd.payoutCount || 0) > 0;
    },
    // Entity-specific formula display (matches backend computeNet).
    formulaText() {
      const money = (n) => this.formatMoneyPlain(n);
      const p = money(this.bd.payinAmount);
      const q = money(this.bd.payoutAmount);
      const pc = money(this.bd.payinCommission);
      const qc = money(this.bd.payoutCommission);
      if (this.selectedEntity && this.selectedEntity.type === 'branch') {
        // payout − payin + payin_comm + payout_comm
        return `${q} − ${p} + ${pc} + ${qc}`;
      }
      // client: payin − payout − payin_comm − payout_comm
      return `${p} − ${q} − ${pc} − ${qc}`;
    },
  },
  methods: {
    todayISO() {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    },
    daysAgoISO(days) {
      const d = new Date();
      d.setDate(d.getDate() - days);
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    },
    initialsOf(u) {
      const s = (u.name || u.username || '?').trim();
      const parts = s.split(/\s+/);
      const a = parts[0]?.[0] || '?';
      const b = parts[1]?.[0] || '';
      return (a + b).toUpperCase();
    },
    formatMoney(v) {
      const n = Math.abs(Number(v) || 0);
      return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    // No absolute — used inside the breakdown formula where sign matters
    // only in the formula symbols, not the individual numbers.
    formatMoneyPlain(v) {
      const n = Number(v) || 0;
      return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatMoneyWithSign(v) {
      const n = Number(v) || 0;
      const sign = n < 0 ? '−' : n > 0 ? '+' : '';
      return sign + this.formatMoney(n);
    },
    formatDateShort(dtStr) {
      if (!dtStr) return '';
      // dtStr is either 'YYYY-MM-DD HH:mm:ss' or a Date. Show as "15 Jul 2026".
      const d = new Date(String(dtStr).replace(' ', 'T'));
      if (isNaN(d.getTime())) return dtStr;
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    signClass(v) {
      const n = Number(v) || 0;
      if (n > 0) return 'txt-credit';
      if (n < 0) return 'txt-debit';
      return 'txt-neutral';
    },
    titleFor(e) {
      if (e.entry_type === 'settlement') return e.category_name || 'Daily Settlement';
      if (e.entry_type === 'reversal') return e.category_name || 'Reversal';
      return e.category_name || 'Manual Entry';
    },
    isSelected(type, id) {
      return this.selectedEntity && this.selectedEntity.type === type && this.selectedEntity.id === id;
    },
    async selectEntity(type, ent) {
      this.$store.commit('ledger/setSelectedEntity', {
        id: ent.id, username: ent.username, name: ent.name, type,
      });
      this.showBreakdown = false;
      await this.refreshCurrent();
    },
    applyPreset(key) {
      this.activePreset = key;
      const today = this.todayISO();
      if (key === 'today') {
        this.filters.startDate = today; this.filters.endDate = today;
      } else if (key === 'yesterday') {
        const y = this.daysAgoISO(1);
        this.filters.startDate = y; this.filters.endDate = y;
      } else if (key === '7d') {
        this.filters.startDate = this.daysAgoISO(6); this.filters.endDate = today;
      } else if (key === '30d') {
        this.filters.startDate = this.daysAgoISO(29); this.filters.endDate = today;
      } else if (key === 'custom') {
        // leave dates as-is, user edits manually
      }
      if (key !== 'custom') this.loadEntries();
    },
    onCustomDate() {
      this.activePreset = 'custom';
      this.loadEntries();
    },
    async refreshCurrent() {
      if (!this.selectedEntity) return;
      try {
        await Promise.all([
          this.$store.dispatch('ledger/loadBalance', {
            entity_type: this.selectedEntity.type,
            entity_id: this.selectedEntity.id,
          }),
          this.loadEntries(),
        ]);
      } catch (e) {
        this.pushToast(e.message, 'error');
      }
    },
    async loadEntries() {
      if (!this.selectedEntity) return;
      try {
        await this.$store.dispatch('ledger/loadEntries', {
          entity_type: this.selectedEntity.type,
          entity_id: this.selectedEntity.id,
          entry_type: this.filters.entry_type || undefined,
          startDate: this.filters.startDate || undefined,
          endDate: this.filters.endDate || undefined,
          page: 1,
          limit: 50,
        });
      } catch (e) {
        this.pushToast(e.message, 'error');
      }
    },
    async gotoPage(page) {
      if (!this.selectedEntity || page < 1 || page > this.totalPages) return;
      try {
        await this.$store.dispatch('ledger/loadEntries', {
          entity_type: this.selectedEntity.type,
          entity_id: this.selectedEntity.id,
          entry_type: this.filters.entry_type || undefined,
          startDate: this.filters.startDate || undefined,
          endDate: this.filters.endDate || undefined,
          page,
          limit: this.pagination.limit,
        });
      } catch (e) {
        this.pushToast(e.message, 'error');
      }
    },
    openManualForm() {
      if (!this.selectedEntity) return;
      this.manualForm = {
        category_id: '', direction: 'credit', amount: 0,
        entry_date: this.todayISO(), notes: '',
      };
      this.showManualForm = true;
    },
    closeManualForm() { this.showManualForm = false; },
    onCategoryChange() {
      const cat = this.categories.find((c) => c.id === Number(this.manualForm.category_id));
      if (cat && cat.direction_default && cat.direction_default !== 'either') {
        this.manualForm.direction = cat.direction_default;
      }
    },
    async submitManual() {
      if (!this.canSubmitManual || this.submitting) return;
      this.submitting = true;
      try {
        await this.$store.dispatch('ledger/postManualEntry', {
          entity_type: this.selectedEntity.type,
          entity_id: this.selectedEntity.id,
          category_id: Number(this.manualForm.category_id),
          direction: this.manualForm.direction,
          amount: Number(this.manualForm.amount),
          notes: this.manualForm.notes || undefined,
          entry_date: this.manualForm.entry_date || undefined,
        });
        this.pushToast('Entry posted successfully.', 'success');
        this.showManualForm = false;
        await this.refreshCurrent();
        if (this.isVendor) await this.$store.dispatch('ledger/loadSummary');
      } catch (e) {
        this.pushToast(e.message, 'error');
      } finally {
        this.submitting = false;
      }
    },
    pushToast(message, type = 'info') {
      this.toast = { message, type };
      setTimeout(() => { this.toast = { message: '', type: '' }; }, 2600);
    },
  },
  async created() {
    this.loading = true;
    // Default range: last 30 days
    this.applyPreset('30d');
    try {
      await this.$store.dispatch('ledger/loadCategories');
      if (this.isVendor) {
        await this.$store.dispatch('ledger/loadSummary');
      } else {
        const type = this.role === 'subadmin' ? 'branch' : 'client';
        this.$store.commit('ledger/setSelectedEntity', {
          id: this.$store.getters.id,
          username: this.$store.getters.username,
          name: this.$store.getters.name,
          type,
        });
        await this.refreshCurrent();
      }
    } catch (e) {
      this.pushToast(e.message, 'error');
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
/* ------------- Shell ------------- */
.ledger-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  gap: 20px;
  overflow-y: auto;
}

/* ------------- Header ------------- */
.ledger-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1B2A47;
  font-weight: 600;
  margin-bottom: 4px;
}
.ledger-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  margin: 0;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
}

/* ------------- Buttons ------------- */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
  outline: none;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-ghost {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-ghost:hover:not(:disabled) { background: #f8fafc; border-color: #cbd5e1; }
/* Secondary — same shell as ghost but with the warm cream palette used
   across the app for non-primary CTAs (matches the Help button in
   AccountHub tabs). Kept alongside .btn-ghost rather than swapping it
   out because other places in this page may still want the cool grey. */
.btn-secondary {
  background: #fff;
  color: #1B2A47;
  border-color: #ede9df;
}
.btn-secondary:hover:not(:disabled) {
  background: #fbfaf7;
  border-color: #d9d3c1;
}
.btn-sm { padding: 6px 12px; font-size: 12px; border-radius: 8px; }
.btn-icon { width: 15px; height: 15px; }

/* ------------- Body layout ------------- */
.ledger-body {
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;
  flex: 1;
  min-height: 0;
}
.ledger-body.is-vendor {
  grid-template-columns: 320px 1fr;
}
@media (max-width: 900px) {
  .ledger-body.is-vendor { grid-template-columns: 1fr; }
}

.loading-block {
  padding: 40px;
  text-align: center;
  color: #64748b;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

/* ------------- Entity panel ------------- */
.entity-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
  max-height: calc(100vh - 200px);
}
.search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.search-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
}
.search-input {
  width: 100%;
  padding: 10px 12px 10px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s;
  background: #f8fafc;
}
.search-input:focus { border-color: #1B2A47; background: #fff; }

.entity-groups { display: flex; flex-direction: column; gap: 16px; overflow-y: auto; }
.entity-group { display: flex; flex-direction: column; gap: 8px; }
.group-head {
  display: flex; justify-content: space-between; align-items: baseline;
  padding: 0 4px;
}
.group-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #475569; }
.group-count {
  background: #f1f5f9; color: #475569;
  padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600;
}
.entity-list { display: flex; flex-direction: column; gap: 4px; }
.entity-row {
  display: grid;
  grid-template-columns: 32px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 10px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.12s;
  border: 1px solid transparent;
}
.entity-row:hover { background: #f8fafc; }
.entity-row.is-active {
  background: linear-gradient(135deg, #eef2f7 0%, #f5f3ff 100%);
  border-color: #cbd5e1;
}
.avatar {
  width: 32px; height: 32px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700;
  color: #fff;
}
.avatar-branch { background: linear-gradient(135deg, #1B2A47, #2C3E63); }
.avatar-client { background: linear-gradient(135deg, #10b981, #059669); }
.entity-meta { min-width: 0; }
.entity-name { font-size: 13px; font-weight: 600; color: #0f172a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.entity-sub  { font-size: 11px; color: #64748b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.entity-balance { text-align: right; }
.balance-label { font-size: 10px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
.balance-value { font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; }
.empty-mini { padding: 12px; text-align: center; font-size: 12px; color: #94a3b8; }

/* ------------- Main area ------------- */
.ledger-main { display: flex; flex-direction: column; gap: 16px; min-width: 0; }

.empty-hero {
  flex: 1;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 60px 24px;
  gap: 12px;
}
.empty-hero-icon {
  width: 60px; height: 60px;
  border-radius: 16px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
}
.empty-hero-icon svg { width: 30px; height: 30px; }
.empty-hero-title { font-size: 16px; font-weight: 600; color: #0f172a; }
.empty-hero-sub { font-size: 13px; color: #64748b; }

/* ------------- Balance card (compact) ------------- */
.balance-card {
  border-radius: 16px;
  padding: 16px 20px;
  color: #fff;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  box-shadow: 0 8px 20px -10px rgba(15, 23, 42, 0.22);
  cursor: pointer;
  transition: box-shadow 0.15s;
}
.balance-card:hover {
  box-shadow: 0 12px 26px -10px rgba(15, 23, 42, 0.28);
}
.balance-card::before {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(circle at 100% 0%, rgba(255,255,255,0.10) 0%, transparent 55%);
  pointer-events: none;
}
.hero-branch { background: linear-gradient(120deg, #1B2A47 0%, #2C3E63 100%); }
.hero-client { background: linear-gradient(120deg, #059669 0%, #0369a1 100%); }

.bc-left {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  min-width: 0;
  flex: 1;
}
.bc-identity {
  display: flex; flex-direction: column; gap: 2px;
  min-width: 0;
}
.bc-badge {
  align-self: flex-start;
  background: rgba(255,255,255,0.18);
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 2px;
}
.bc-name {
  font-size: 15px; font-weight: 700;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bc-sub { font-size: 11px; opacity: 0.7; }

.bc-live {
  display: flex; flex-direction: column; gap: 3px;
  padding-left: 20px;
  border-left: 1px solid rgba(255,255,255,0.15);
}
.bc-live-label {
  font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase;
  opacity: 0.7; font-weight: 600;
}
.bc-live-value {
  font-size: 24px; font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  color: #fff;
}

.bc-right {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  flex-shrink: 0;
}
.bc-mini { display: flex; flex-direction: column; gap: 2px; text-align: right; }
.mini-label {
  font-size: 9px; opacity: 0.7; letter-spacing: 0.08em;
  text-transform: uppercase; font-weight: 600;
}
.mini-value {
  font-size: 13px; font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #fff;
}
.bc-divider {
  width: 1px; height: 26px;
  background: rgba(255,255,255,0.18);
}
.bc-refresh {
  border: 1px solid rgba(255,255,255,0.22);
  background: rgba(255,255,255,0.08);
  color: #fff;
  width: 30px; height: 30px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
  transition: all 0.12s;
}
.bc-refresh:hover { background: rgba(255,255,255,0.16); }
.bc-refresh svg { width: 14px; height: 14px; }

/* Mobile: stack */
@media (max-width: 720px) {
  .balance-card { flex-direction: column; align-items: flex-start; gap: 14px; }
  .bc-live { border-left: 0; padding-left: 0; }
  .bc-right { width: 100%; justify-content: space-between; }
}

/* ------------- Pending breakdown panel ------------- */
.pending-explainer {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.explain-head {
  display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;
}
.explain-title { font-size: 13px; font-weight: 700; color: #0f172a; }
.explain-sub   { font-size: 12px; color: #64748b; margin-top: 2px; }
.explain-sub b { color: #334155; font-weight: 600; }
.explain-close {
  border: none; background: transparent; cursor: pointer;
  font-size: 20px; color: #94a3b8; line-height: 1;
  width: 24px; height: 24px; border-radius: 6px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
}
.explain-close:hover { background: #f1f5f9; color: #475569; }

.explain-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}
.explain-cell {
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 10px 12px;
}
.cell-label {
  font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
  color: #64748b; font-weight: 600;
}
.cell-value {
  font-size: 15px; font-weight: 700; color: #0f172a;
  font-variant-numeric: tabular-nums; margin-top: 3px;
}
.cell-sub {
  font-size: 11px; color: #94a3b8; margin-top: 2px;
}
@media (max-width: 720px) {
  .explain-grid { grid-template-columns: repeat(2, 1fr); }
}

.explain-formula {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  border-top: 1px dashed #e2e8f0;
  padding-top: 12px;
  font-size: 13px;
}
.formula-label {
  background: #eef2f7; color: #1B2A47;
  padding: 3px 10px; border-radius: 999px;
  font-size: 11px; font-weight: 700;
  letter-spacing: 0.06em; text-transform: uppercase;
}
.formula-body {
  color: #475569; font-variant-numeric: tabular-nums;
  font-family: 'SFMono-Regular', Menlo, monospace; font-size: 12px;
}
.formula-eq { color: #94a3b8; font-weight: 700; }
.formula-result {
  font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums;
}
.explain-empty {
  font-size: 12px; color: #94a3b8; padding: 8px 4px;
}

.explain-fade-enter-active, .explain-fade-leave-active {
  transition: all 0.18s ease;
}
.explain-fade-enter-from, .explain-fade-leave-to {
  opacity: 0; transform: translateY(-6px);
}

.pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: capitalize;
}
.pill-sm { padding: 2px 8px; font-size: 10px; }
.pill-credit { background: #d1fae5; color: #065f46; }
.pill-debit  { background: #fee2e2; color: #991b1b; }

/* ------------- Filters bar ------------- */
.filters-bar {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}
.filter-presets { display: flex; gap: 6px; flex-wrap: wrap; }
.chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
}
.chip:hover { border-color: #cbd5e1; background: #f8fafc; }
.chip-active {
  background: #eef2f7;
  border-color: #cbd5e1;
  color: #1B2A47;
  font-weight: 600;
}
.filter-inputs { display: flex; gap: 10px; flex-wrap: wrap; }
.filter-field {
  display: flex; flex-direction: column; gap: 4px;
}
.filter-field span { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; font-weight: 600; }
.filter-field input, .filter-field select {
  padding: 6px 10px; border: 1px solid #e2e8f0; border-radius: 8px;
  font-size: 12px; background: #f8fafc; outline: none;
}
.filter-field input:focus, .filter-field select:focus { border-color: #1B2A47; background: #fff; }

/* ------------- Entries ------------- */
.entries-section { display: flex; flex-direction: column; gap: 12px; }
.entries-list { display: flex; flex-direction: column; gap: 10px; }

.entry-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  transition: all 0.12s;
}
.entry-card:hover { border-color: #cbd5e1; box-shadow: 0 4px 12px -6px rgba(15, 23, 42, 0.08); }
.entry-card.type-reversal { border-left: 3px solid #f97316; }
.entry-card.type-manual { border-left: 3px solid #3b82f6; }
.entry-card.type-settlement { border-left: 3px solid #10b981; }

.entry-left { display: flex; gap: 12px; align-items: flex-start; min-width: 0; }
.entry-icon {
  width: 36px; height: 36px; flex-shrink: 0;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
}
.entry-icon svg { width: 18px; height: 18px; }
.icon-settlement { background: #d1fae5; color: #059669; }
.icon-reversal   { background: #ffedd5; color: #ea580c; }
.icon-manual     { background: #dbeafe; color: #2563eb; }
.entry-headline { min-width: 0; flex: 1; }
.entry-title-row { display: flex; align-items: center; gap: 8px; }
.entry-title { font-size: 14px; font-weight: 600; color: #0f172a; }
.entry-meta {
  font-size: 12px; color: #64748b; margin-top: 4px;
  display: flex; gap: 6px; flex-wrap: wrap;
}
.dot-sep { color: #cbd5e1; }
.mono { font-family: 'SFMono-Regular', Menlo, monospace; font-size: 11px; }
.entry-notes { margin-top: 6px; font-size: 12px; color: #475569; font-style: italic; }

.entry-right { text-align: right; min-width: 160px; }
.entry-amount {
  font-size: 20px; font-weight: 700; font-variant-numeric: tabular-nums;
  display: inline-flex; align-items: baseline; gap: 3px;
}
.amt-sign { font-size: 16px; opacity: 0.7; }
.amt-credit { color: #059669; }
.amt-debit  { color: #dc2626; }
.entry-breakdown {
  margin-top: 8px; display: grid; grid-template-columns: 1fr 1fr; gap: 4px 12px;
  text-align: right; font-size: 11px; color: #64748b;
}
.brk { display: flex; justify-content: flex-end; gap: 6px; }
.brk-lbl { font-weight: 500; color: #94a3b8; }
.brk-val { color: #475569; font-variant-numeric: tabular-nums; }
.brk-val em { font-style: normal; color: #94a3b8; font-size: 10px; }

/* ------------- Pagination ------------- */
.pagination-bar {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 4px;
}
.pag-info { font-size: 12px; color: #64748b; }
.pag-controls { display: flex; gap: 6px; }

/* ------------- Empty entries ------------- */
.empty-entries {
  background: #fff; border: 1px dashed #cbd5e1;
  padding: 40px 20px; border-radius: 14px;
  text-align: center;
}
.empty-mini-icon {
  width: 48px; height: 48px;
  margin: 0 auto 12px;
  border-radius: 12px; background: #f1f5f9; color: #94a3b8;
  display: flex; align-items: center; justify-content: center;
}
.empty-mini-icon svg { width: 24px; height: 24px; }
.empty-title { font-size: 14px; font-weight: 600; color: #0f172a; }
.empty-sub   { font-size: 12px; color: #64748b; margin-top: 4px; }

/* ------------- Text sign colors ------------- */
.txt-credit  { color: #059669 !important; }
.txt-debit   { color: #dc2626 !important; }
.txt-neutral { color: #475569 !important; }

/* ------------- Modal ------------- */
.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  z-index: 50;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.modal-card {
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 60px -20px rgba(15, 23, 42, 0.4);
  overflow: hidden;
  display: flex; flex-direction: column;
}
.modal-head {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;
}
.modal-eyebrow { font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: #1B2A47; font-weight: 700; }
.modal-title { font-size: 18px; font-weight: 700; color: #0f172a; margin-top: 4px; }
.modal-close {
  border: none; background: transparent; cursor: pointer;
  width: 32px; height: 32px; border-radius: 8px; color: #94a3b8;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.12s;
}
.modal-close:hover { background: #f1f5f9; color: #475569; }
.modal-close svg { width: 18px; height: 18px; }

.modal-body { padding: 20px 24px; }
.form-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px 16px;
}
.form-field { display: flex; flex-direction: column; gap: 6px; }
.form-field.span-2 { grid-column: 1 / -1; }
.form-field label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em;
  font-weight: 600; color: #475569;
}
.form-field label .opt { color: #94a3b8; text-transform: none; letter-spacing: normal; font-weight: 500; }
.form-field input, .form-field select, .form-field textarea {
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;
}
.form-field input:focus, .form-field select:focus, .form-field textarea:focus {
  border-color: #1B2A47; background: #fff;
}

.direction-toggle {
  display: grid; grid-template-columns: 1fr 1fr;
  border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden;
  background: #f8fafc;
}
.dir-btn {
  padding: 10px 12px;
  border: none; background: transparent; cursor: pointer;
  font-size: 12px; font-weight: 600; color: #64748b;
  transition: all 0.12s;
}
.dir-btn:hover { background: #f1f5f9; }
.dir-btn.dir-active.dir-credit { background: #059669; color: #fff; }
.dir-btn.dir-active.dir-debit  { background: #dc2626; color: #fff; }

.modal-foot {
  padding: 16px 24px;
  border-top: 1px solid #f1f5f9;
  display: flex; justify-content: flex-end; gap: 8px;
  background: #f8fafc;
}

/* ------------- Toast ------------- */
.ledger-toast {
  position: fixed;
  top: 24px; right: 24px;
  z-index: 60;
  background: #0f172a; color: #fff;
  padding: 12px 18px;
  border-radius: 12px;
  font-size: 13px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 10px;
  box-shadow: 0 12px 30px -10px rgba(15, 23, 42, 0.4);
}
.ledger-toast .dot {
  width: 8px; height: 8px; border-radius: 999px; background: #94a3b8;
}
.ledger-toast.is-success { background: #059669; }
.ledger-toast.is-success .dot { background: #a7f3d0; }
.ledger-toast.is-error   { background: #dc2626; }
.ledger-toast.is-error .dot { background: #fecaca; }

/* ------------- Transitions ------------- */
.toast-enter-active, .toast-leave-active {
  transition: all 0.24s cubic-bezier(0.4, 0, 0.2, 1);
}
.toast-enter-from, .toast-leave-to { transform: translateY(-12px); opacity: 0; }
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.18s ease; }
.modal-fade-enter-active .modal-card, .modal-fade-leave-active .modal-card {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-from .modal-card, .modal-fade-leave-to .modal-card {
  transform: translateY(20px) scale(0.98);
}
</style>
